import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, ProfileContext }  from '../../App';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, renderColumnHeader, TableProps, useReactTable } from '../Grid';
import { PostResponse } from '../Models';
import { ErrorNotification, SuccessNotification } from '../Notification';
import { handleGetFileResponse }  from '../../util/File';
import { formatDate } from '../../util/Format';
import {get, deleete} from '../../util/HttpRequest'
import { Button, Modal } from 'antd';
import Waiting from '../Waiting';
import GridSummary from '../GridSummary';
import GridPagination from '../GridPagination';
import ClaimDocumentUpload from './DocumentUpload'
import { UploadIcon, DeleteIcon, ExclamationIcon, FileIcon, FilePdfIcon } from '../Icons'

class ClaimDocumentData {
    processStatus: string = "";
    documentId: number = 0;
    claimId: number = 0;
    documentDateUTC: string = "";
    description: string = "";
    fileExtension: string = "";
    fileContentType: string = "";
    processMessage: string = "";
    showDocument: boolean = false;
    allowDelete: boolean = false;
    matchesFilter: boolean = true;
};

class ClaimDocumentsData {
    recordCount: number = 0;
    refreshDate: Date = new Date();
    searchResults: ClaimDocumentData[] = [];
};

class ClaimDocumentsFilter {
    processStatus: string[] = [];
};

function ClaimDocuments(props) {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [gridFilter, setGridFilter] = useState(new ClaimDocumentsFilter());
    const [gridData, setGridData] = useState(new ClaimDocumentsData());
    const {accessToken, extendSession, user} = useContext(AuthContext);
    const {languagePreference, userProfileLossDraftsClaim} = useContext(ProfileContext);
    const {t} = useTranslation(["ClaimDocuments", "DataGrid", "Form"]);
    const { confirm } = Modal;
    const columnHeaderProps = [
        {
            id: 'documentDateUTC', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'description', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        },
        {
            id: 'processMessage', className: 'datagrid-header-left', sortedDescLabel: t('DataGrid:lblSortedDescending'), sortedAscLabel: t('DataGrid:lblSortedAscending'), unsortedLabel: t('DataGrid:lblUnsorted')
        }
    ];

    useEffect (() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languagePreference, userProfileLossDraftsClaim.claimId]);
    
    function refreshData() {
        setLoading(true);
        getData();
    };

    function setProcessStatusFilter(processStatus) {
        const filter = {...gridFilter, processStatus: processStatus};
        setGridFilter(filter);
        applyFilters(gridData.searchResults, filter);
    };
    
    function applyFilters(data, filter: ClaimDocumentsFilter) {
        if (filter.processStatus.length === 0) {
            data.forEach(d => {
                d.matchesFilter = true;
            });
        }
        else {
            data.forEach(d => {
                d.matchesFilter = (filter.processStatus.includes(d.processStatus));
            });
        }
        return data;
    };

    function getData() {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimDocuments");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());

        console.log("Fetch Claim Documents from " + url);

        get(url, accessToken)
        .then( response => {
            if (!response.ok) { throw response }
            return response.json() as Promise<ClaimDocumentData[]>
        })
        .then( searchResults => {
            const filteredSearchResults = applyFilters(searchResults, gridFilter);
            const data = {
                recordCount: filteredSearchResults.length, 
                refreshDate: new Date(),
                searchResults: filteredSearchResults
            };
            setGridData(data);
            setErrorMsg("");
            setLoading(false);
        })
        .catch( error => {
            setGridData(new ClaimDocumentsData());
            setErrorMsg(error.message);
            setLoading(false);
        })
    };

    function handleDocumentUpload() {
        extendSession();
        props.showUploadDocumentModal();
    }

    function getDocumentFromAPI(processStatus, documentId, isDownload) {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimDocumentFile");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());
        url.searchParams.set("ProcessStatus", processStatus);
        url.searchParams.set("DocumentId", documentId.toString());

        console.log("Fetch Claim Document File from " + url);
        
        get(url, accessToken)
        .then((response) => {
            handleGetFileResponse(response, isDownload);
        })
        .catch( error => {
            setErrorMsg(error.message);
        });
    };

    function viewDocument(event, processStatus, documentId) {
        event.preventDefault();
        getDocumentFromAPI(processStatus, documentId, false);
    };

    function deleteDocumentConfirm(event, processStatus, documentId) {
        event.preventDefault();

        confirm({
            autoFocusButton: 'cancel',
            cancelButtonProps: {type: "default"},
            cancelText: t('Form:btnCancel'),
            icon: <ExclamationIcon class="medium-icon color-red" />,
            maskClosable: true,
            okButtonProps: {className: "button submit"},
            okText: t('actionDelete'),
            onOk() {
              deleteDocumentConfirmOK(processStatus, documentId)
            },
            title: t('confirmDelete')
          });
    }

    function deleteDocumentConfirmOK(processStatus, documentId) {
        deleteDocument(processStatus, documentId);
    }
    
    function deleteDocument(processStatus, documentId) {
        extendSession();
        
        const url = new URL(import.meta.env.VITE_CCP_API_CCP + "/ClaimDocument");
        url.searchParams.set("UserId", user.id);
        url.searchParams.set("ClaimId", userProfileLossDraftsClaim.claimId.toString());
        url.searchParams.set("ProcessStatus", processStatus);
        url.searchParams.set("DocumentId", documentId.toString());

        console.log("Delete Claim Document at " + url);
    
        deleete(url, accessToken)
        .then((response) => {
            if (!response.ok) { throw new Error(response.status + " - " + response.statusText)}
            return response.json() as Promise<PostResponse>;
        })
        .then(response => {
            if (response.success) {
                refreshData();
                SuccessNotification(t('deleteSuccessTitle'), t('deleteSuccessBody'));
            }
            else {
                ErrorNotification(t('deleteFailureTitle'), t('deleteFailureBody'));
            }
        })
        .catch(error => {
            ErrorNotification(t('deleteFailureTitle'), t('deleteFailureBody'));
        });
    };

    function Table<TData extends object>({ columns, data }: TableProps<TData>) {
         const pageSize = Number(import.meta.env.VITE_GRID_PAGE_SIZE);
        // Use the state and functions returned from useReactTable to build your UI
        const table = useReactTable({
            data,
            columns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
            getSortedRowModel: getSortedRowModel(),
            initialState: {
                pagination: {
                    pageIndex: 0, // Start on first page
                    pageSize: pageSize,
                },
            }, 
        });

        const filters = <div>
            <Button className="button filter" disabled={gridFilter.processStatus.includes("ETRM") || gridFilter.processStatus.includes("UPLD")} onClick={() => setProcessStatusFilter(["ETRM", "UPLD"])}>{t('filterAwaitingReview')}</Button>
            <Button className="button filter" disabled={gridFilter.processStatus.includes("ACPT")} onClick={() => setProcessStatusFilter(["ACPT"])}>{t('filterAccepted')}</Button>
            <Button className="button filter" disabled={gridFilter.processStatus.includes("RJUP") || gridFilter.processStatus.includes("RJDS")} onClick={() => setProcessStatusFilter(["RJUP", "RJDS"])}>{t('filterRejected')}</Button>
            <Button className="button filter" disabled={gridFilter.processStatus.length === 0} onClick={() => setProcessStatusFilter([])}>{t('filterAll')}</Button>
        </div>;

        const pagination = (
            <GridPagination
                canPreviousPage={table.getCanPreviousPage()}
                canNextPage={table.getCanNextPage()}
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                gotoPage={table.setPageIndex}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
            />
        );

        const summary = (
            <GridSummary
                pageIndex={table.getState().pagination.pageIndex}
                pageCount={table.getPageCount()}
                pageSize={pageSize}
                recordCount={data.length}
                recordType={t('gridRecordType', { count: data.length })}
                refreshDate={gridData.refreshDate}
                pagination={pagination}
            />
        );

        return (
            <>
                {filters}
                <br />
                {summary}
                <table className="table" role="table">
                    <thead role="rowgroup">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} role="row">
                                {headerGroup.headers.map((header) =>
                                    renderColumnHeader(header, columnHeaderProps.find(x => x.id === header.column.id))
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody role="rowgroup">
                        {table.getRowModel().rows.map((row, i) => (
                            <React.Fragment key={row.id}>
                                <tr className={"row-" + (i % 2 === 0 ? "even" : "odd")} role="row" tabIndex={0}>
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} role="cell">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </>
        )
    }

    function renderDataGrid() {
        const columns = [{
            id: 'actions',
            enableSorting: false,
            cell:  ({ row }) => (
                <div className="datagrid-data-center">
                {
                    row.original.showDocument && 
                    (
                        <Button type="link" className="icon-button" title={t('actionView')} 
                            onClick={(e) => viewDocument(e, row.original.processStatus, row.original.documentId)}
                        >
                             { 
                                row.original.fileExtension === "PDF" && (
                                    <FilePdfIcon title={t('actionView')} />
                                )
                            }
                            { 
                                row.original.fileExtension !== "PDF" && (
                                    <FileIcon title={t('actionView')} />
                                )
                            }
                        </Button>
                    )
                }
                {
                    !row.original.showDocument && 
                    (<span className="action-icon" />)
                }
                {
                    row.original.allowDelete && 
                    (
                        <Button type="link" className="icon-button" title={t('actionDelete')} 
                            onClick={(e) => deleteDocumentConfirm(e, row.original.processStatus, row.original.documentId)}
                        >
                            <DeleteIcon title={t('actionDelete')} />
                        </Button>
                    )
                }
                {
                    !row.original.allowDelete && 
                    (<span className="action-icon" />)
                }
                </div>
            )
        }, { 
            accessorKey: 'documentDateUTC',
            header: () => (<>{t('columnDate')}</>),
            enableSorting: true,
            cell: (info) => (
                <div className="datagrid-data-left">
                {
                    formatDate(info.getValue())
                }
                </div>
            )
        }, {
            accessorKey: 'description',
            header: () => (<>{t('columnDescription')}</>),
            enableSorting: true
        }, {
            accessorKey: 'processMessage',
            header: () => (<>{t('columnStatus')}</>),
            enableSorting: true
        }];
       
        const table = <Table
            columns={columns}
            data={gridData.searchResults.filter(item => item.matchesFilter)} 
        />;

        const modal = props.documentTypeSelectOptions.hasLoaded && props.documentUploadModalShow &&
        (
            <ClaimDocumentUpload 
                documentTypeSelectOptions={props.documentTypeSelectOptions}
                refreshData={refreshData} 
                hideModal={props.hideUploadDocumentModal}
            />
        );

        return <div>{table}{modal}</div>;
    };

    const title = <>
        <h2>{t("title")}</h2>
        {
            !errorMsg && !loading && props.documentTypeSelectOptions.hasLoaded &&
            (
                <span>
                    <Button 
                        className="button submit" 
                        icon={<UploadIcon class="button-icon" title={t('actionUpload')} />}
                        type="primary"
                        onClick={() => handleDocumentUpload()}>
                            {t('actionUpload')}
                    </Button>
                </span>

            )
        }
        <br /><br />
    </>;
    const contents = (errorMsg > '') ? <p><em>{errorMsg}</em></p> : loading ? <Waiting size='large' /> : renderDataGrid();
    return <div>{title}<br />{contents}</div>
};

export default ClaimDocuments;
